import { TextButton } from '@creditornot/cb-button';
import { CalendarClock, ChainLink, FoodCoffee, RotatingArrows, ShareAndroid } from '@creditornot/cb-icons';
import { colors } from '@creditornot/cb-ingredients';
import { Spinner } from '@creditornot/cb-progress';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { Typography as T } from 'common/typography';
import { localize } from 'common/utils';

import { reportMenuSyncHistoryOpen } from '../behavioral';
import { useSharedMenuLink } from '../hooks/useSharedMenuLink';
import { useMenuSyncContext } from '../menu-sync/MenuSyncContext';
import SyncHistoryModal from '../menu-sync/SyncHistoryModal';
import { MenuSyncStatus } from '../menu-sync/types';

const Root = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-end: 32px;
`;

const LoadingPlaceholderContainer = styled.div`
  height: 64px;
  margin-block-end: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const spin = keyframes`
    from {
        transform:rotate(0deg);
    }
    to {
        transform:rotate(360deg);
    }
`;

const StyledRotatingArrowsIcon = styled(RotatingArrows)`
  animation-name: ${spin};
  animation-duration: 5000ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  height: 12px;
`;

const SyncStatusButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const StyledChainLink = styled(ChainLink)`
  height: 12px;
  width: 12px;
  flex-shrink: 0;
`;

const PULL_WARNING_TOAST_ID = 'pull-warning-toast';
const PULL_SUCCESS_TOAST_ID = 'pull-success-toast';

const MenuStatus = () => {
  const { locale } = useIntl();
  const { syncStatus, isLoadingSyncStatus } = useMenuSyncContext();
  const { sharedMenuLink } = useSharedMenuLink();

  const oldStatusRef = useRef<MenuSyncStatus | 'unset'>('unset');

  const [syncTypeHistoryToShow, setSyncTypeHistoryToShow] = useState<'push' | 'pull' | null>(null);

  useEffect(() => {
    if (syncStatus?.status === 'pull-in-progress' || syncStatus?.status === 'pull-scheduled') {
      toast(<FormattedMessage id="menu.menu-sync.pull-in-progress-warning" />, {
        duration: Infinity,
        id: PULL_WARNING_TOAST_ID,
      });
    }

    if (
      syncStatus?.status === 'pull-success' &&
      syncStatus.status !== oldStatusRef.current &&
      oldStatusRef.current !== 'unset'
    ) {
      toast.success(<FormattedMessage id="menu.menu-sync.pull-success-notification" />, {
        duration: Infinity,
        id: PULL_SUCCESS_TOAST_ID,
      });
      toast.dismiss(PULL_WARNING_TOAST_ID);
    }

    if (syncStatus?.status) {
      oldStatusRef.current = syncStatus?.status;
    }
  }, [syncStatus?.status]);

  const handleOpenPushHistory = useCallback(() => {
    setSyncTypeHistoryToShow('push');
    reportMenuSyncHistoryOpen('push');
  }, []);

  const handleOpenPullHistory = useCallback(() => {
    setSyncTypeHistoryToShow('pull');
    reportMenuSyncHistoryOpen('pull');
  }, []);

  if (isLoadingSyncStatus) {
    return (
      <LoadingPlaceholderContainer>
        <Spinner size="medium" />
      </LoadingPlaceholderContainer>
    );
  }

  return (
    <Root>
      <T.Heading2>
        <FormattedMessage id="menu.menu-sync.status-header" />
      </T.Heading2>
      {!!sharedMenuLink && sharedMenuLink.selection_is_shared && (
        <TextButton style={{ margin: '4px 0', textAlign: 'start' }} icon={<ShareAndroid />} wrap>
          {sharedMenuLink.selection_name.length > 0 ? (
            <FormattedMessage
              id="listing-manager.selections.venue-editor.classic.using-listing-manager"
              values={{ name: localize(sharedMenuLink.selection_name, locale) }}
            />
          ) : (
            <FormattedMessage id="shared-menu.link.venue.label" />
          )}
        </TextButton>
      )}
      {syncStatus?.status === 'push-in-progress' && (
        <SyncStatusButton data-testid="status-push-in-progress">
          <StyledRotatingArrowsIcon fill={colors.wolt100} />
          <TextButton onClick={handleOpenPushHistory} style={{ marginInlineStart: '8px' }}>
            <FormattedMessage id="menu.menu-sync.status.push-in-progress" />
          </TextButton>
        </SyncStatusButton>
      )}
      {syncStatus?.status === 'pull-in-progress' && (
        <SyncStatusButton data-testid="status-pull-in-progress">
          <StyledRotatingArrowsIcon fill={colors.wolt100} />
          <TextButton onClick={handleOpenPullHistory} style={{ marginInlineStart: '8px' }}>
            <FormattedMessage id="menu.menu-sync.status.pull-in-progress" />
          </TextButton>
        </SyncStatusButton>
      )}
      {syncStatus?.status === 'pull-scheduled' && (
        <SyncStatusButton data-testid="status-pull-scheduled">
          <FoodCoffee height={12} width={12} fill={colors.orange100} />
          <TextButton onClick={handleOpenPullHistory} style={{ marginInlineStart: '8px' }} variant="orange">
            <FormattedMessage id="menu.menu-sync.status.pull-scheduled" />
          </TextButton>
        </SyncStatusButton>
      )}
      {syncStatus?.status === 'push-scheduled' && syncStatus.latestIntent && (
        <SyncStatusButton data-testid="status-push-scheduled">
          <CalendarClock height={12} width={12} fill={colors.orange100} />
          <TextButton onClick={handleOpenPushHistory} style={{ marginInlineStart: '8px' }} variant="orange">
            <FormattedMessage id="menu.menu-sync.status.push-scheduled" />
          </TextButton>
        </SyncStatusButton>
      )}
      {syncStatus?.status === 'push-success' && syncStatus.latestIntent && (
        <SyncStatusButton data-testid="status-push-success">
          <StyledChainLink fill={colors.wolt100} />
          <TextButton onClick={handleOpenPushHistory} style={{ marginInlineStart: '8px' }}>
            <FormattedMessage
              id="menu.menu-sync.status.push-success"
              values={{
                date: DateTime.fromISO(syncStatus.latestIntent.updated_at).toLocaleString(DateTime.DATETIME_SHORT),
              }}
            />
          </TextButton>
        </SyncStatusButton>
      )}
      {syncStatus?.status === 'push-failure' && syncStatus.latestIntent && (
        <SyncStatusButton data-testid="status-push-failure">
          <StyledChainLink fill={colors.strawberry100} />
          <TextButton variant="red" onClick={handleOpenPushHistory} style={{ marginInlineStart: '8px' }}>
            <FormattedMessage
              id="menu.menu-sync.status.push-failure"
              values={{
                date: DateTime.fromISO(syncStatus.latestIntent.updated_at).toLocaleString(DateTime.DATETIME_SHORT),
              }}
            />
          </TextButton>
        </SyncStatusButton>
      )}
      {syncStatus?.status === 'pull-success' && syncStatus.latestIntent && (
        <SyncStatusButton data-testid="status-pull-success">
          <StyledChainLink fill={colors.wolt100} />
          <TextButton onClick={handleOpenPullHistory} style={{ marginInlineStart: '8px' }}>
            <FormattedMessage
              id="menu.menu-sync.status.pull-success"
              values={{
                date: DateTime.fromISO(syncStatus.latestIntent.updated_at).toLocaleString(DateTime.DATETIME_SHORT),
              }}
            />
          </TextButton>
        </SyncStatusButton>
      )}
      {syncStatus?.status === 'pull-failure' && syncStatus.latestIntent && (
        <SyncStatusButton data-testid="status-pull-failure">
          <StyledChainLink fill={colors.strawberry100} />
          <TextButton variant="red" onClick={handleOpenPullHistory} style={{ marginInlineStart: '8px' }}>
            <FormattedMessage
              id="menu.menu-sync.status.pull-failure"
              values={{
                date: DateTime.fromISO(syncStatus.latestIntent.updated_at).toLocaleString(DateTime.DATETIME_SHORT),
              }}
            />
          </TextButton>
        </SyncStatusButton>
      )}
      {syncTypeHistoryToShow && (
        <SyncHistoryModal initialMode={syncTypeHistoryToShow} onClose={() => setSyncTypeHistoryToShow(null)} />
      )}
    </Root>
  );
};

export default MenuStatus;
